import React from "react";
import { Briefcase, Lock } from "lucide-react";
import {
  EMPLOYMENT_STATUS, INDUSTRY, YEARS_EXPERIENCE, EMPLOYER_TENURE, EDUCATION, BUSINESS_OWNERSHIP,
  INCOME_RANGE, INCOME_SOURCES, FINANCIAL_LIFESTYLE, FINANCIAL_GOALS, FINANCE_RELATIONSHIP, labelOf,
} from "../lib/careerFinancialOptions";

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-white/6 last:border-0">
      <span className="text-[11px] uppercase tracking-wide text-[#9E978E] shrink-0">{label}</span>
      <span className="text-sm text-[#D8D2C9] text-right min-w-0 break-words">{value}</span>
    </div>
  );
}

export default function CareerFinancialDisplay({ cf }) {
  if (!cf) return null;
  const careerRows = [
    ["Employment", labelOf(EMPLOYMENT_STATUS, cf.employment_status)],
    ["Industry", labelOf(INDUSTRY, cf.industry)],
    ["Title", cf.professional_title],
    ["Experience", labelOf(YEARS_EXPERIENCE, cf.years_experience)],
    ["Tenure", labelOf(EMPLOYER_TENURE, cf.employer_tenure)],
    ["Education", labelOf(EDUCATION, cf.education)],
    ["Business", labelOf(BUSINESS_OWNERSHIP, cf.business_ownership)],
  ].filter(([, v]) => v);
  const incomeSources = (cf.income_sources || []).map((k) => labelOf(INCOME_SOURCES, k)).filter(Boolean);
  const goals = (cf.financial_goals || []).map((k) => labelOf(FINANCIAL_GOALS, k)).filter(Boolean);
  const finRows = [
    ["Annual income (USD, self-reported)", labelOf(INCOME_RANGE, cf.income_range)],
    ["Financial lifestyle", labelOf(FINANCIAL_LIFESTYLE, cf.financial_lifestyle)],
    ["Approach to finances", labelOf(FINANCE_RELATIONSHIP, cf.finance_relationship_approach)],
  ].filter(([, v]) => v);
  const hasFinancial = finRows.length > 0 || incomeSources.length > 0 || goals.length > 0;
  if (careerRows.length === 0 && !hasFinancial) return null;

  return (
    <div className="rounded-2xl border border-white/8 bg-[#141113]/60 p-5" data-testid="career-financial-display">
      <h3 className="font-serif text-lg text-[#FBF8F3] mb-1 flex items-center gap-2">
        <Briefcase size={16} className="text-[#D4AF37]" /> Career & Financial
      </h3>
      <p className="text-[10px] text-[#9E978E] mb-3 flex items-center gap-1"><Lock size={10} /> Shared voluntarily · self-reported, not verified</p>
      {careerRows.length > 0 && (
        <div className="mb-3">
          <h4 className="text-[11px] uppercase tracking-wide text-[#C9A96E] mb-1">Professional Background</h4>
          {careerRows.map(([l, v]) => <Row key={l} label={l} value={v} />)}
        </div>
      )}
      {hasFinancial && (
        <div>
          <h4 className="text-[11px] uppercase tracking-wide text-[#C9A96E] mb-1">Financial Profile</h4>
          {finRows.map(([l, v]) => <Row key={l} label={l} value={v} />)}
          {incomeSources.length > 0 && <Row label="Income sources" value={incomeSources.join(", ")} />}
          {goals.length > 0 && <Row label="Financial goals" value={goals.join(", ")} />}
        </div>
      )}
    </div>
  );
}
